import { Link } from "react-router";
import { ArrowLeft, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export function ChipPosFileFormatDocPage() {
  return (
    <div className="container mx-auto px-6 py-8 max-w-4xl">
      <div className="mb-8">
        <Button asChild variant="ghost" className="mb-4">
          <Link to="/docs" className="flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Documentation
          </Link>
        </Button>

        <div className="flex items-center mb-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Chip Pos File Format</h1>
            <p className="text-xl text-gray-600">ASCII wafer layout specification</p>
          </div>
        </div>
      </div>

      {/* Overview */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Overview</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none">
          <p>
            A <code>.chip_pos</code> file is an ASCII text document that depicts a wafer layout and
            indicates which dies to measure, in what order, and where the reference die is located.
            This format is used by measurement equipment to determine the sequence of die
            measurements on a silicon wafer.
          </p>
        </CardContent>
      </Card>

      {/* Warning about notation types */}
      <Card className="mb-6 border-amber-200 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center text-amber-900">
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            Supported Notation Type
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-amber-900 font-semibold">
            There are two types of chip_pos file notation:
          </p>
          <div className="space-y-3">
            <div className="bg-white p-3 rounded border border-green-200">
              <p className="text-sm font-semibold text-green-900 mb-1">
                ✓ Ordered Notation (Supported)
              </p>
              <ul className="list-disc list-inside text-sm text-green-900 space-y-1 ml-2">
                <li>
                  Reference die: <code className="bg-green-100 px-1 rounded">o0</code>
                </li>
                <li>
                  Selected dies with order: <code className="bg-green-100 px-1 rounded">+1</code>,{" "}
                  <code className="bg-green-100 px-1 rounded">+2</code>,{" "}
                  <code className="bg-green-100 px-1 rounded">+3</code>, ...
                </li>
              </ul>
            </div>
            <div className="bg-white p-3 rounded border border-red-200">
              <p className="text-sm font-semibold text-red-900 mb-1">
                ✗ Unordered Notation (Not Supported)
              </p>
              <ul className="list-disc list-inside text-sm text-red-900 space-y-1 ml-2">
                <li>
                  Reference die: <code className="bg-red-100 px-1 rounded">0</code>
                </li>
                <li>
                  Selected dies without order: <code className="bg-red-100 px-1 rounded">X</code>
                </li>
              </ul>
            </div>
          </div>
          <p className="text-sm text-amber-900">
            <strong>Important:</strong> If you upload a chip_pos file using the unordered notation,
            you will receive an error message. Please convert your file to use the ordered notation
            format before uploading.
          </p>
        </CardContent>
      </Card>

      {/* File Structure */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>File Structure</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-700">The file consists of two main sections:</p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
            <li>
              <strong>Header</strong> (lines 1-6): Metadata about the wafer and die dimensions
            </li>
            <li>
              <strong>ASCII Map</strong> (line 8+): Visual representation of the wafer layout
            </li>
          </ol>
        </CardContent>
      </Card>

      {/* Example File */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Example File</CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-xs font-mono">
            {`C= ORYX9
W= WAFER_ALL
X= 21840
Y= 21840
D= 200000
F= bottom

      - - - -
  - - - + +5 - - -
  - +4 + + + + +6 -
- - + + + + + + - -
- +3 + + + o0 + + + -
- + + + +1 + + + +7 -
- - + + + + + + - -
  - + +2 + + + +8 -
    - - + + - -
      - - - -`}
          </pre>
        </CardContent>
      </Card>

      {/* Header Values */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Header Values</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4 font-semibold">Field</th>
                  <th className="text-left py-2 px-4 font-semibold">Description</th>
                  <th className="text-left py-2 px-4 font-semibold">Example</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">C=</code>
                  </td>
                  <td className="py-2 px-4">Configuration/mask name</td>
                  <td className="py-2 px-4">
                    <code>ORYX9</code>
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">W=</code>
                  </td>
                  <td className="py-2 px-4">Wafer selection (which wafers to measure)</td>
                  <td className="py-2 px-4">
                    <code>WAFER_ALL</code>
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">X=</code>
                  </td>
                  <td className="py-2 px-4">Die width in micrometers (µm)</td>
                  <td className="py-2 px-4">
                    <code>21840</code>
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">Y=</code>
                  </td>
                  <td className="py-2 px-4">Die height in micrometers (µm)</td>
                  <td className="py-2 px-4">
                    <code>21840</code>
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">D=</code>
                  </td>
                  <td className="py-2 px-4">Wafer diameter in micrometers (µm)</td>
                  <td className="py-2 px-4">
                    <code>200000</code> (200mm wafer)
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">F=</code>
                  </td>
                  <td className="py-2 px-4">Flat/notch orientation</td>
                  <td className="py-2 px-4">
                    <code>bottom</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ASCII Map Symbols */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>ASCII Map Symbols</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-700 mb-4">
            The ASCII map represents the wafer layout where each symbol indicates a die position:
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-4 font-semibold">Symbol</th>
                  <th className="text-left py-2 px-4 font-semibold">Meaning</th>
                  <th className="text-left py-2 px-4 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">&nbsp;</code> (space)
                  </td>
                  <td className="py-2 px-4">Empty</td>
                  <td className="py-2 px-4">No die at this position (outside wafer boundary)</td>
                </tr>
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">-</code>
                  </td>
                  <td className="py-2 px-4">Unmeasurable</td>
                  <td className="py-2 px-4">
                    Die exists but is not measurable (e.g., edge die, excluded zone)
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">+</code>
                  </td>
                  <td className="py-2 px-4">Measurable</td>
                  <td className="py-2 px-4">Die is measurable but not selected for measurement</td>
                </tr>
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">+N</code>
                  </td>
                  <td className="py-2 px-4">Selected</td>
                  <td className="py-2 px-4">
                    Die is selected for measurement with order N (e.g., <code>+1</code>,{" "}
                    <code>+2</code>, <code>+3</code>, ...)
                  </td>
                </tr>
                <tr>
                  <td className="py-2 px-4">
                    <code className="bg-gray-100 px-2 py-1 rounded">o0</code>
                  </td>
                  <td className="py-2 px-4">Reference</td>
                  <td className="py-2 px-4">
                    Reference die (always measured first, order 0). This is the origin point (0,0)
                    for the coordinate system
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Measurement Order */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Measurement Order</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
            <li>
              The <strong>reference die</strong> (<code>o0</code>) is always measured first (order
              0)
            </li>
            <li>
              Selected dies (<code>+N</code>) are measured in numerical order: <code>+1</code>,{" "}
              <code>+2</code>, <code>+3</code>, etc.
            </li>
            <li>
              Unselected measurable dies (<code>+</code>) and unmeasurable dies (<code>-</code>) are
              skipped
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Coordinate System */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Coordinate System</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
            <li>
              The reference die (<code>o0</code>) defines the origin (0,0) of the coordinate system
            </li>
            <li>Column indices increase to the right</li>
            <li>Row indices increase downward</li>
            <li>
              Die positions are expressed relative to the reference die (e.g., <code>2:-1</code>{" "}
              means 2 columns right, 1 row up from reference)
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="list-disc list-inside space-y-2 text-sm text-gray-700">
            <li>The ASCII map uses single spaces as separators between symbols</li>
            <li>Two consecutive spaces indicate an empty cell (no die)</li>
            <li>The map shape typically reflects the circular wafer boundary</li>
            <li>
              Dies at the wafer edge are often marked as unmeasurable (<code>-</code>) due to edge
              exclusion zones
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
